import lookTables from '../data/skin_render/look_tables.json';
import {
    CLASS_RENDER_PROFILES,
    PLAYER_LOOK_BONE_ID,
    buildCharacterColors,
    normalizeSex,
} from './skinRenderShared';

/** Build a Dofus look string. `skinIds` are render skin ids for the visible
 *  equipment (hat/cape), taken from `item.render_skin_id`. Items with no mapping
 *  are simply absent — the character renders without that piece rather than
 *  wearing whatever skin happens to share the item's id.
 *  Guild/alliance emblem skins are out of scope and intentionally omitted. */
export function buildLookStringFromVisuals({ className, characterSex, skinIds, accentRgb = null }) {
    const profile = className ? CLASS_RENDER_PROFILES[className] : null;
    if (!profile) return null;

    const equipSkins = (Array.isArray(skinIds) ? skinIds : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);

    const sex = normalizeSex(characterSex);
    const sexKey = String(sex);
    const headId = profile.head[sex];
    const bodyId = profile.body[sex];

    const headSkin = lookTables.headSkins?.[className]?.[sexKey]?.[String(headId)];
    const bodySkin = lookTables.bodySkins?.[className]?.[sexKey]?.[String(bodyId)];
    if (headSkin == null || bodySkin == null) return null;

    const skins = [bodySkin, headSkin, ...equipSkins];
    const colors = buildCharacterColors(profile, sex, accentRgb);
    const colorPart = colors.map((c, idx) => `${idx + 1}=${c}`).join(',');
    const scale = lookTables.scales?.[className]?.[sexKey] ?? 100;

    return `{${PLAYER_LOOK_BONE_ID}|${skins.join(',')}|${colorPart}|${scale}}`;
}

/** The skeleton to render this class on.
 *
 *  Bone `1` resolves to the generic `1-static`, which carries one anonymous idle
 *  and one fists-up combat stance. Each class also has its own `1-<breed>-static`
 *  — already extracted, previously unused — holding that class's poses: the
 *  combat stance with its effects (an Osamodas summon, a Xelor's orb) and two
 *  class idles. Same files, same bytes; the look string just never asked for
 *  them, because it only carries the bone *id* and every player is bone 1.
 */
export function getClassBoneName(className) {
    const breed = className ? CLASS_RENDER_PROFILES[className]?.breed : null;
    return Number.isFinite(Number(breed)) ? `${PLAYER_LOOK_BONE_ID}-${breed}-static` : null;
}

export function getLookCacheKey(lookString) {
    return lookString || '';
}
